import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { authHelper } from '../utils/authHelper';

const API = process.env.REACT_APP_BACKEND_URL;

function CourseViewer() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCourse();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const loadCourse = async () => {
    try {
      const res = await fetch(`${API}/api/lms/courses/${courseId}`, {
        headers: authHelper.getAuthHeaders()
      });
      const data = await res.json();
      setCourse(data);

      // Auto-select first unlocked lesson
      if (data.modules && data.modules.length > 0) {
        for (const module of data.modules) {
          if (module.is_unlocked) {
            const lessonsRes = await fetch(`${API}/api/lms/modules/${module.module_id}/lessons`, {
              headers: authHelper.getAuthHeaders()
            });
            const lessonsData = await lessonsRes.json();
            if (lessonsData.lessons && lessonsData.lessons.length > 0) {
              setSelectedLesson(lessonsData.lessons[0]);
              break;
            }
          }
        }
      }
    } catch (err) {
      console.error('Error loading course:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLesson = async (lesson, moduleId) => {
    try {
      const res = await fetch(`${API}/api/lms/modules/${moduleId}/lessons`, {
        headers: authHelper.getAuthHeaders()
      });
      const data = await res.json();
      const fullLesson = data.lessons.find(l => l.lesson_id === lesson.lesson_id);
      setSelectedLesson(fullLesson);
    } catch (err) {
      console.error('Error loading lesson:', err);
    }
  };

  const handleMarkComplete = async () => {
    if (!selectedLesson) return;

    try {
      const res = await fetch(`${API}/api/lms/lessons/${selectedLesson.lesson_id}/complete`, {
        method: 'POST',
        headers: authHelper.getAuthHeaders()
      });
      
      if (res.ok) {
        // Reload course to update progress
        await loadCourse();
        alert('¡Lección completada! ✅');
      }
    } catch (err) {
      console.error('Error marking lesson complete:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#02040A] flex items-center justify-center">
        <p className="text-white text-xl">Cargando curso...</p>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-[#02040A] flex items-center justify-center">
        <p className="text-white text-xl">Curso no encontrado</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#02040A]">
      <div className="flex flex-col lg:flex-row h-screen">
        {/* Sidebar - Modules & Lessons */}
        <div className="lg:w-80 bg-gray-900 border-r border-gray-800 overflow-y-auto">
          <div className="p-4 border-b border-gray-800">
            <button
              onClick={() => window.location.href = '#/lms'}
              className="text-[#C5A14C] hover:underline text-sm mb-3"
            >
              ← Volver a cursos
            </button>
            <h2 className="text-xl font-bold text-white mb-1">{course.title}</h2>
            
            {/* Progress */}
            {course.enrollment && (
              <div className="mt-3">
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>Progreso</span>
                  <span className="text-[#C5A14C] font-semibold">{course.enrollment.overall_progress}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-[#C5A14C] h-2 rounded-full transition-all"
                    style={{ width: `${course.enrollment.overall_progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Modules List */}
          <div className="p-2">
            {course.modules.map((module, idx) => (
              <ModuleAccordion
                key={module.module_id}
                module={module}
                index={idx}
                onSelectLesson={(lesson) => handleSelectLesson(lesson, module.module_id)}
                selectedLesson={selectedLesson}
              />
            ))}
          </div>
        </div>

        {/* Main Content - Video Player */}
        <div className="flex-1 overflow-y-auto">
          {selectedLesson ? (
            <div className="p-6 max-w-5xl mx-auto">
              {/* Video Player */}
              <div className="bg-gray-900 rounded-lg overflow-hidden mb-6">
                <div className="aspect-video bg-black flex items-center justify-center">
                  {selectedLesson.video_url.startsWith('http') ? (
                    <iframe
                      src={selectedLesson.video_url}
                      className="w-full h-full"
                      allowFullScreen
                      title={selectedLesson.title}
                    />
                  ) : (
                    <video
                      controls
                      className="w-full h-full"
                      src={selectedLesson.video_url}
                    >
                      Tu navegador no soporta el reproductor de video.
                    </video>
                  )}
                </div>
              </div>

              {/* Lesson Info */}
              <div className="bg-gray-800 rounded-lg p-6 mb-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h1 className="text-3xl font-bold text-white mb-2">{selectedLesson.title}</h1>
                    <p className="text-gray-400">{selectedLesson.description}</p>
                  </div>
                  {!selectedLesson.is_completed && (
                    <button
                      onClick={handleMarkComplete}
                      className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold whitespace-nowrap"
                    >
                      ✓ Marcar Completa
                    </button>
                  )}
                  {selectedLesson.is_completed && (
                    <span className="px-4 py-2 bg-green-900 text-green-300 rounded-lg font-semibold">
                      ✅ Completada
                    </span>
                  )}
                </div>

                {/* Duration */}
                <div className="flex items-center text-gray-400 text-sm">
                  <span>⏱️ Duración: {Math.floor(selectedLesson.duration / 60)} minutos</span>
                </div>
              </div>

              {/* Resources */}
              {selectedLesson.resources && selectedLesson.resources.length > 0 && (
                <div className="bg-gray-800 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-white mb-4">📎 Recursos</h3>
                  <div className="space-y-2">
                    {selectedLesson.resources.map((resource) => (
                      <a
                        key={resource.url}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 bg-gray-900 rounded hover:bg-gray-700 transition-colors"
                      >
                        <span className="text-[#C5A14C]">📄 {resource.title}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <span className="text-6xl block mb-4">📚</span>
                <p className="text-gray-400 text-lg">Selecciona una lección para comenzar</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ModuleAccordion({ module, index, onSelectLesson, selectedLesson }) {
  const [isOpen, setIsOpen] = useState(module.is_unlocked);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && module.is_unlocked && lessons.length === 0) {
      loadLessons();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadLessons = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/lms/modules/${module.module_id}/lessons`, {
        headers: authHelper.getAuthHeaders()
      });
      const data = await res.json();
      setLessons(data.lessons || []);
    } catch (err) {
      console.error('Error loading lessons:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-2">
      <button
        onClick={() => module.is_unlocked && setIsOpen(!isOpen)}
        className={`w-full p-4 text-left rounded-lg transition-colors ${
          module.is_unlocked
            ? 'bg-gray-800 hover:bg-gray-750'
            : 'bg-gray-900 opacity-60 cursor-not-allowed'
        }`}
        disabled={!module.is_unlocked}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-white font-semibold">
                {module.is_unlocked ? '📖' : '🔒'} Módulo {module.order}
              </span>
              {module.is_completed && <span className="text-green-400">✅</span>}
            </div>
            <p className="text-sm text-gray-400">{module.title}</p>
            {!module.is_unlocked && (
              <p className="text-xs text-yellow-500 mt-1">Desbloquea en semana {module.unlock_week}</p>
            )}
          </div>
          {module.is_unlocked && (
            <span className="text-gray-500">{isOpen ? '▼' : '▶'}</span>
          )}
        </div>
      </button>

      {/* Lessons */}
      {isOpen && module.is_unlocked && (
        <div className="ml-4 mt-2 space-y-1">
          {loading ? (
            <p className="text-gray-500 text-sm p-2">Cargando lecciones...</p>
          ) : lessons.length === 0 ? (
            <p className="text-gray-500 text-sm p-2">No hay lecciones todavía</p>
          ) : (
            lessons.map(lesson => (
              <button
                key={lesson.lesson_id}
                onClick={() => onSelectLesson(lesson)}
                className={`w-full p-3 text-left rounded transition-colors ${
                  selectedLesson?.lesson_id === lesson.lesson_id
                    ? 'bg-[#C5A14C] text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <div className="flex items-center gap-2">
                  {lesson.is_completed ? (
                    <span className="text-green-400">✅</span>
                  ) : (
                    <span className="text-gray-500">⭕</span>
                  )}
                  <span className="text-sm">{lesson.title}</span>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default CourseViewer;
